/**
 * VeracrossUpload.jsx
 * SuperAdmin tool for uploading Veracross CSV exports into Firestore.
 * Supports students, staff, and sections collections.
 * CSV parsing happens client-side; writes happen server-side via Cloud Function.
 *
 * Sections are special: the CSV arrives as one row per enrollment (a student in
 * a section), and this tool GROUPS those rows into one document per section with
 * a studentIds array before uploading. See groupSections().
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

const functions = getFunctions();
const fnUpload  = httpsCallable(functions, "csvUpload");
const fnHistory = httpsCallable(functions, "csvUploadHistory");

// ─── Design tokens ─────────────────────────────────────────────────────────
const FONT_DISPLAY = "'Playfair Display', Georgia, serif";
const FONT_DATA    = "'Rajdhani', 'Trebuchet MS', sans-serif";
const FONT_BODY    = "'EB Garamond', Georgia, serif";
const FONT_MONO    = "'Courier New', Courier, monospace";

const C = {
  ink:    "#1a1714",
  cream:  "#faf7f2",
  gold:   "#b8924a",
  navy:   "#1a3a5c",
  muted:  "#7a7068",
  border: "#e8e2d8",
  bg:     "#f4f0e8",
  green:  "#22863a",
  red:    "#8b2c2c",
  amber:  "#b8924a",
};

// ─── Collection definitions ────────────────────────────────────────────────
const COLLECTIONS = {
  students: {
    label: "Students",
    icon: "🎓",
    idField: "sisId",
    description: "Student records — keyed by SIS ID",
    expectedFields: ["sisId", "firstName", "lastName", "name", "grade", "advisor", "email", "cohort"],
    firestorePath: "/students/{sisId}",
  },
  staff: {
    label: "Staff / Teachers",
    icon: "👤",
    idField: "sisId",
    description: "Staff and teacher records — keyed by SIS ID",
    expectedFields: ["sisId", "firstName", "lastName", "name", "department", "email", "role", "title"],
    firestorePath: "/staff/{sisId}",
  },
  sections: {
    label: "Sections / Classes",
    icon: "📚",
    idField: "sectionId",
    description: "Class sections — one row per enrollment, grouped into a roster",
    expectedFields: ["sectionId", "studentId", "teacherId", "block", "course"],
    firestorePath: "/sections/{sectionId}",
    // Grouping config: many enrollment rows -> one section doc with an array.
    grouped: true,
    rosterField: "studentId",      // per-enrollment column collected into the array
    rosterInto: "studentIds",      // array field on the section document
    carryFields: ["teacherId", "block", "course"], // same for every row of a section
  },
};

// ─── CSV parser ────────────────────────────────────────────────────────────
function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) throw new Error("CSV must have a header row and at least one data row.");

  const parseRow = (line) => {
    const cols = [];
    let cur = "";
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuote && line[i + 1] === '"') { cur += '"'; i++; }
        else inQuote = !inQuote;
      } else if (ch === "," && !inQuote) {
        cols.push(cur.trim());
        cur = "";
      } else {
        cur += ch;
      }
    }
    cols.push(cur.trim());
    return cols;
  };

  const headers = parseRow(lines[0]).map(h => h.replace(/^["']|["']$/g, "").trim());
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const values = parseRow(line);
    const row = {};
    headers.forEach((h, idx) => {
      const val = (values[idx] || "").replace(/^["']|["']$/g, "").trim();
      if (val !== "") row[h] = val;
    });
    if (Object.keys(row).length > 0) rows.push(row);
  }

  return { headers, rows };
}

// ─── Section grouping ────────────────────────────────────────────────────────
// One row per enrollment -> one document per section with a studentIds array.
// The carry fields (teacher, block, course) are taken from the section's rows.
function groupSections(rows, config) {
  const map = {};
  rows.forEach((r) => {
    const id = r[config.idField];
    if (!id) return;
    if (!map[id]) {
      map[id] = { [config.idField]: id, [config.rosterInto]: [] };
      config.carryFields.forEach((f) => { if (r[f] !== undefined) map[id][f] = r[f]; });
    }
    // Fill any carry field that wasn't present on the first row of this section.
    config.carryFields.forEach((f) => { if (map[id][f] === undefined && r[f] !== undefined) map[id][f] = r[f]; });
    const sid = r[config.rosterField];
    if (sid && !map[id][config.rosterInto].includes(sid)) map[id][config.rosterInto].push(sid);
  });
  return Object.values(map);
}

// ─── Field mapping badge ───────────────────────────────────────────────────
function FieldBadge({ field, isIdField, isExpected, isPresent }) {
  const bg   = isIdField  ? C.navy  : isExpected && isPresent ? "#e8f4e8" : isExpected ? "#fff3e0" : C.bg;
  const color = isIdField ? C.cream : isExpected && isPresent ? C.green   : isExpected ? C.amber   : C.muted;
  const border = isIdField ? C.navy : isExpected && isPresent ? C.green   : isExpected ? C.amber   : C.border;

  return (
    <span style={{ fontFamily: FONT_MONO, fontSize: 11, padding: "2px 8px", borderRadius: 3, background: bg, color, border: `1px solid ${border}`, display: "inline-block", margin: "2px 3px" }}>
      {isIdField && "🔑 "}{field}
    </span>
  );
}

// ─── Upload history row ────────────────────────────────────────────────────
function HistoryRow({ record }) {
  const date = record.uploadedAt
    ? new Date(record.uploadedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: `1px solid ${C.border}`, fontFamily: FONT_DATA, fontSize: 12 }}>
      <div style={{ width: 80, color: C.muted, fontSize: 11 }}>{date}</div>
      <div style={{ width: 70, fontWeight: 600, color: C.navy, textTransform: "capitalize" }}>{record.collection}</div>
      <div style={{ flex: 1, color: C.muted }}>
        {record.fields?.join(", ").slice(0, 60)}{record.fields?.join(", ").length > 60 ? "…" : ""}
      </div>
      <div style={{ color: C.green, fontWeight: 600 }}>{record.rowsWritten} written</div>
      {record.rowsSkipped > 0 && <div style={{ color: C.amber }}>{record.rowsSkipped} skipped</div>}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
export default function VeracrossUpload() {
  const [collection,    setCollection]    = useState("students");
  const [file,          setFile]          = useState(null);
  const [parsed,        setParsed]        = useState(null); // { headers, rows }
  const [parseError,    setParseError]    = useState("");
  const [uploading,     setUploading]     = useState(false);
  const [uploadResult,  setUploadResult]  = useState(null);
  const [uploadError,   setUploadError]   = useState("");
  const [history,       setHistory]       = useState([]);
  const [historyLoading,setHistoryLoading]= useState(true);
  const [dragOver,      setDragOver]      = useState(false);

  const fileInputRef = useRef(null);
  const config = COLLECTIONS[collection];
  const isGrouped = !!config.grouped;

  // Rows actually written: grouped into sections when applicable, else raw.
  const uploadRows = parsed ? (isGrouped ? groupSections(parsed.rows, config) : parsed.rows) : [];
  // Headers shown in the preview reflect the written shape, not the raw CSV.
  const previewHeaders = isGrouped
    ? [config.idField, ...config.carryFields, config.rosterInto]
    : (parsed?.headers || []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await fnHistory();
        setHistory(data || []);
      } catch (e) {
        console.warn("Could not load history:", e.message);
      } finally {
        setHistoryLoading(false);
      }
    })();
  }, [uploadResult]);

  const handleFile = useCallback((f) => {
    if (!f) return;
    if (!f.name.endsWith(".csv")) {
      setParseError("Please upload a .csv file.");
      return;
    }
    setFile(f);
    setParsed(null);
    setParseError("");
    setUploadResult(null);
    setUploadError("");

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const result = parseCSV(e.target.result);
        setParsed(result);
      } catch (err) {
        setParseError(err.message);
      }
    };
    reader.readAsText(f);
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleUpload = async () => {
    if (!parsed || uploading) return;
    setUploading(true);
    setUploadError("");
    setUploadResult(null);

    try {
      // For sections, send the grouped section documents (with studentIds
      // arrays), not the raw enrollment rows.
      const { data } = await fnUpload({ collection, rows: uploadRows });
      setUploadResult(data);
      setParsed(null);
      setFile(null);
    } catch (e) {
      setUploadError(e.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  // Field analysis runs on the RAW csv headers (the enrollment columns).
  const expectedFields  = config.expectedFields;
  const presentFields   = parsed?.headers || [];
  const missingExpected = expectedFields.filter(f => !presentFields.includes(f));
  const hasIdField      = presentFields.includes(config.idField);
  const extraFields     = presentFields.filter(f => !expectedFields.includes(f));

  const S = {
    wrap:   { fontFamily: FONT_BODY, background: C.cream, minHeight: "100vh", color: C.ink },
    header: { background: C.ink, padding: "2rem 2rem 1.6rem", borderBottom: `3px solid ${C.gold}` },
    main:   { maxWidth: 1000, margin: "0 auto", padding: "2rem", display: "grid", gridTemplateColumns: "240px 1fr", gap: "1.5rem", alignItems: "start" },
    sidebar:{ position: "sticky", top: "1rem" },
    card:   { background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, padding: "1.2rem", marginBottom: "1rem", boxShadow: "0 1px 6px rgba(0,0,0,0.05)" },
  };

  const cell = (v) => Array.isArray(v)
    ? `${v.length} student${v.length === 1 ? "" : "s"}`
    : (v || <span style={{ color: C.border }}>—</span>);

  return (
    <div style={S.wrap}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,400&family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Rajdhani:wght@400;500;600&display=swap');
        * { box-sizing: border-box; }
        button { cursor: pointer; }
      `}</style>

      <header style={S.header}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.25em", color: C.gold, textTransform: "uppercase", marginBottom: 8 }}>
            King's Academy · Veracross Import
          </div>
          <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(1.4rem,3vw,2rem)", fontWeight: 700, color: C.cream, margin: "0 0 6px", lineHeight: 1.15 }}>
            SIS Data <em style={{ color: C.gold }}>Upload</em>
          </h1>
          <p style={{ fontFamily: FONT_BODY, fontStyle: "italic", color: "rgba(250,247,242,0.5)", fontSize: 14, margin: 0 }}>
            Upload Veracross CSV exports to Firestore · Merge mode · SuperAdmin only
          </p>
        </div>
      </header>

      <main style={S.main}>

        <div style={S.sidebar}>

          <div style={S.card}>
            <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 10 }}>
              Target Collection
            </div>
            {Object.entries(COLLECTIONS).map(([key, col]) => (
              <button
                key={key}
                onClick={() => { setCollection(key); setParsed(null); setFile(null); setUploadResult(null); setUploadError(""); setParseError(""); }}
                style={{ width: "100%", textAlign: "left", padding: "10px 12px", marginBottom: 6, borderRadius: 2, border: `1px solid ${collection === key ? C.navy : C.border}`, background: collection === key ? "#e8f0f8" : C.cream, fontFamily: FONT_DATA, fontSize: 12, fontWeight: 600, color: collection === key ? C.navy : C.muted }}
              >
                <div style={{ fontSize: 16, marginBottom: 2 }}>{col.icon}</div>
                <div>{col.label}</div>
                <div style={{ fontWeight: 400, fontSize: 10, color: C.muted, marginTop: 2 }}>{col.description}</div>
              </button>
            ))}
          </div>

          <div style={S.card}>
            <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 8 }}>
              Expected Fields
            </div>
            <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted, marginBottom: 8 }}>
              Firestore path: <span style={{ fontFamily: FONT_MONO, color: C.navy }}>{config.firestorePath}</span>
            </div>
            <div>
              {config.expectedFields.map(f => (
                <FieldBadge
                  key={f}
                  field={f}
                  isIdField={f === config.idField}
                  isExpected={true}
                  isPresent={presentFields.includes(f)}
                />
              ))}
            </div>
            <div style={{ marginTop: 10, fontFamily: FONT_DATA, fontSize: 10, color: C.muted, lineHeight: 1.6 }}>
              🔑 = document ID field (required)<br />
              <span style={{ color: C.green }}>■</span> = present in CSV<br />
              <span style={{ color: C.amber }}>■</span> = missing from CSV<br />
              Extra fields are also stored.
            </div>
            {isGrouped && (
              <div style={{ marginTop: 10, fontFamily: FONT_DATA, fontSize: 10, color: C.navy, lineHeight: 1.6, background: "#e8f0f8", borderRadius: 3, padding: "6px 8px" }}>
                One row per enrollment. Rows are grouped by <code style={{ fontFamily: FONT_MONO }}>{config.idField}</code> into
                one document per section, collecting <code style={{ fontFamily: FONT_MONO }}>{config.rosterField}</code> values
                into a <code style={{ fontFamily: FONT_MONO }}>{config.rosterInto}</code> array.
              </div>
            )}
          </div>

          <div style={S.card}>
            <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 8 }}>
              Recent Uploads
            </div>
            {historyLoading ? (
              <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: C.muted }}>Loading…</div>
            ) : history.length === 0 ? (
              <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: C.muted }}>No uploads yet.</div>
            ) : (
              history.slice(0, 8).map(r => <HistoryRow key={r.id} record={r} />)
            )}
          </div>
        </div>

        <div>

          {uploadResult && (
            <div style={{ background: "#e8f4e8", border: `1px solid ${C.green}`, borderRadius: 3, padding: "16px 20px", marginBottom: 20, fontFamily: FONT_DATA }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.green, marginBottom: 4 }}>✓ Upload complete</div>
              <div style={{ fontSize: 12, color: C.ink }}>
                <strong>{uploadResult.written}</strong> records written to <code style={{ fontFamily: FONT_MONO, background: C.bg, padding: "1px 5px", borderRadius: 2 }}>{collection}</code> collection
                {uploadResult.deduplicated > 0 && <span style={{ color: C.navy }}> · {uploadResult.deduplicated} duplicate rows merged</span>}
                {uploadResult.skipped > 0 && <span style={{ color: C.amber }}> · {uploadResult.skipped} skipped (missing ID)</span>}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                All existing fields not in this CSV were preserved (merge mode).
              </div>
            </div>
          )}

          {(uploadError || parseError) && (
            <div style={{ background: "#fff5f5", border: `1px solid ${C.red}`, borderRadius: 3, padding: "12px 16px", marginBottom: 20, fontFamily: FONT_DATA, fontSize: 13, color: C.red }}>
              ⚠ {uploadError || parseError}
            </div>
          )}

          {!parsed && (
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{ border: `2px dashed ${dragOver ? C.navy : C.border}`, borderRadius: 4, padding: "48px 32px", textAlign: "center", background: dragOver ? "#e8f0f8" : "#fff", cursor: "pointer", transition: "all 0.15s", marginBottom: 20 }}
            >
              <div style={{ fontSize: 36, marginBottom: 12 }}>📂</div>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.1rem", fontWeight: 600, color: C.ink, marginBottom: 6 }}>
                Drop your Veracross CSV here
              </div>
              <div style={{ fontFamily: FONT_BODY, fontStyle: "italic", fontSize: 13, color: C.muted, marginBottom: 16 }}>
                or click to browse — .csv files only
              </div>
              <div style={{ display: "inline-block", fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, padding: "8px 20px", background: C.navy, color: C.cream, borderRadius: 2 }}>
                Select File
              </div>
              <input ref={fileInputRef} type="file" accept=".csv" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
            </div>
          )}

          {parsed && (
            <div>
              <div style={{ ...S.card, borderLeft: `3px solid ${C.gold}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                <div>
                  <div style={{ fontFamily: FONT_DATA, fontSize: 13, fontWeight: 600, color: C.ink }}>{file?.name}</div>
                  <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: C.muted, marginTop: 2 }}>
                    {isGrouped
                      ? `${parsed.rows.length.toLocaleString()} enrollments → ${uploadRows.length.toLocaleString()} sections`
                      : `${parsed.rows.length.toLocaleString()} rows · ${parsed.headers.length} columns`}
                  </div>
                </div>
                <button onClick={() => { setParsed(null); setFile(null); setParseError(""); setUploadError(""); }}
                  style={{ fontFamily: FONT_DATA, fontSize: 11, padding: "5px 12px", border: `1px solid ${C.border}`, borderRadius: 2, background: C.bg, color: C.muted }}>
                  ✕ Remove
                </button>
              </div>

              <div style={S.card}>
                <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 10 }}>
                  Field Analysis
                </div>

                {!hasIdField && (
                  <div style={{ background: "#fff5f5", border: `1px solid ${C.red}`, borderRadius: 2, padding: "8px 12px", marginBottom: 10, fontFamily: FONT_DATA, fontSize: 12, color: C.red }}>
                    ✗ Required ID field <code style={{ fontFamily: FONT_MONO }}>{config.idField}</code> not found in CSV. Upload blocked.
                  </div>
                )}

                {missingExpected.filter(f => f !== config.idField).length > 0 && (
                  <div style={{ background: "#fff8ed", border: `1px solid ${C.amber}`, borderRadius: 2, padding: "8px 12px", marginBottom: 10, fontFamily: FONT_DATA, fontSize: 12, color: C.ink }}>
                    ⚠ Expected columns not in CSV:{" "}
                    {missingExpected.filter(f => f !== config.idField).map(f => (
                      <code key={f} style={{ fontFamily: FONT_MONO, background: "#fff3e0", padding: "1px 4px", borderRadius: 2, marginLeft: 4 }}>{f}</code>
                    ))}
                  </div>
                )}

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted, marginBottom: 4 }}>All columns in CSV:</div>
                  {parsed.headers.map(f => (
                    <FieldBadge
                      key={f}
                      field={f}
                      isIdField={f === config.idField}
                      isExpected={expectedFields.includes(f)}
                      isPresent={true}
                    />
                  ))}
                </div>

                {extraFields.length > 0 && (
                  <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted, marginTop: 6 }}>
                    {extraFields.length} extra column(s) not in the expected list{isGrouped ? " are ignored for sections." : " will also be stored in Firestore."}
                  </div>
                )}
              </div>

              <div style={S.card}>
                <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 10 }}>
                  Preview (first 5 {isGrouped ? "sections" : "rows"})
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_DATA, fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: C.bg, borderBottom: `2px solid ${C.border}` }}>
                        {previewHeaders.map(h => (
                          <th key={h} style={{ padding: "6px 10px", textAlign: "left", color: h === config.idField ? C.navy : C.muted, fontWeight: 600, whiteSpace: "nowrap" }}>
                            {h === config.idField ? "🔑 " : ""}{h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {uploadRows.slice(0, 5).map((row, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? "#fff" : C.cream }}>
                          {previewHeaders.map(h => (
                            <td key={h} style={{ padding: "5px 10px", color: h === config.idField ? C.navy : C.ink, fontWeight: h === config.idField ? 600 : 400, whiteSpace: "nowrap", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
                              {cell(row[h])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {uploadRows.length > 5 && (
                  <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: C.muted, marginTop: 8, textAlign: "right" }}>
                    + {(uploadRows.length - 5).toLocaleString()} more {isGrouped ? "sections" : "rows"}
                  </div>
                )}
              </div>

              <div style={{ ...S.card, background: C.bg, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontFamily: FONT_DATA, fontSize: 13, fontWeight: 600, color: C.ink }}>
                    Ready to upload {uploadRows.length.toLocaleString()} {isGrouped ? "sections" : "records"} to{" "}
                    <code style={{ fontFamily: FONT_MONO, background: "#fff", padding: "1px 6px", borderRadius: 2, color: C.navy }}>/{collection}</code>
                  </div>
                  <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: C.muted, marginTop: 3 }}>
                    Merge mode — existing fields not in this upload are preserved{isGrouped ? "; each section's roster is replaced with the uploaded one." : "."}
                  </div>
                </div>
                <button
                  onClick={handleUpload}
                  disabled={uploading || !hasIdField}
                  style={{ fontFamily: FONT_DATA, fontSize: 13, fontWeight: 600, padding: "10px 24px", background: uploading || !hasIdField ? C.muted : C.navy, color: C.cream, border: "none", borderRadius: 2, opacity: !hasIdField ? 0.5 : 1, whiteSpace: "nowrap" }}
                >
                  {uploading ? "Uploading…" : `Upload ${uploadRows.length.toLocaleString()} ${isGrouped ? "Sections" : "Records"} →`}
                </button>
              </div>
            </div>
          )}

          {!parsed && !uploadResult && (
            <div style={{ padding: "16px 20px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, fontFamily: FONT_BODY, fontSize: 13, color: C.muted, lineHeight: 1.75 }}>
              <strong style={{ fontFamily: FONT_DATA, fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>How it works</strong>
              Export a CSV from Veracross, select the target collection, drop the file above, review the mapping and
              preview, then upload. Records use merge mode — existing Firestore fields not in the CSV are never overwritten.
              For <strong>Sections</strong>, the CSV is one row per enrollment; the tool groups those rows by <code style={{ fontFamily: FONT_MONO }}>sectionId</code> into
              one document per section holding a <code style={{ fontFamily: FONT_MONO }}>studentIds</code> array, plus the teacher, block, and course.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}